<template>
  <HeaderBar />

  <ButtonBackContainer>
    <ButtonBack :fallback="{ name: 'yieldManagement' }">{{ t('back') }}</ButtonBack>
  </ButtonBackContainer>

  <section class="carvia-container">
    <div v-if="vehicleExtras">
      <div class="flex flex-col gap-4">
        <h2>{{ t('additionalDrivers') }}</h2>
        <EditExtraForm
          :initial-extra-dto="getExtraDto(VehicleExtraType.ADDITIONAL_DRIVER)"
          :has-quantity-options="true"
          @change="hasChanged[0] = $event"
        />
      </div>
      <Divider />
      <div class="flex flex-col gap-4">
        <h2>{{ t('crossBorder') }}</h2>
        <EditExtraForm
          :initial-extra-dto="getExtraDto(VehicleExtraType.CROSSBORDER)"
          @change="hasChanged[1] = $event"
        />
      </div>
      <Divider />
      <div class="flex flex-col gap-4">
        <h2>{{ t('deductible') }}</h2>
        <EditExtraForm
          :initial-extra-dto="getExtraDto(VehicleExtraType.DEDUCTIBLE)"
          @change="hasChanged[2] = $event"
        />
      </div>
      <Divider />
      <div class="flex flex-col gap-4">
        <h2>{{ t('windowAndMobilityInsurance') }}</h2>
        <EditExtraForm
          :initial-extra-dto="getExtraDto(VehicleExtraType.WINDOW_AND_MOBILITY_INSURANCE)"
          @change="hasChanged[3] = $event"
        />
      </div>
      <Divider />
      <div class="flex flex-col gap-4">
        <h2>{{ t('youngDriver') }}</h2>
        <EditExtraForm
          :initial-extra-dto="getExtraDto(VehicleExtraType.YOUNG_DRIVER)"
          :has-quantity-options="true"
          @change="hasChanged[4] = $event"
        />
      </div>
      <Divider />
      <div class="flex flex-col gap-4">
        <h2>{{ t('extraKm') }}</h2>
        <EditExtraForm
          :initial-extra-dto="getExtraDto(VehicleExtraType.EXTRA_KM)"
          @change="hasChanged[5] = $event"
        />
      </div>
    </div>
    <div v-else class="flex items-center justify-center py-8"><Spinner /></div>
  </section>
</template>

<script setup lang="ts">
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import { useI18n } from 'vue-i18n';
import { useUiStore } from '@/stores/ui.store.ts';
import { watchEffect } from 'vue';
import { useVehicleExtras } from '@/queries/use-vehicle-extras.ts';
import Spinner from '@/components/icons/Spinner.vue';
import EditExtraForm from './EditExtraForm.vue';
import Divider from '@/components/Divider.vue';
import {
  type EditAutoAssignVehicleExtraDto,
  type ExtraPriceRangeDto,
  VehicleExtraType,
} from '@carvia/ros-client-types';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import { usePreventLeavingUnsavedForm } from '@/hooks/use-prevent-leaving-unsaved-form.ts';
import { useMoneyClicks } from '@/hooks/use-money-clicks.ts';

export type EditExtraDto = Omit<EditAutoAssignVehicleExtraDto, 'priceRanges'> & {
  priceRanges: (ExtraPriceRangeDto & { toPrice?: number | null; maxQuantity?: number })[];
};

const { t } = useI18n();
const uiStore = useUiStore();
useMoneyClicks();

const { data: vehicleExtras } = useVehicleExtras({
  types: [
    VehicleExtraType.ADDITIONAL_DRIVER,
    VehicleExtraType.CROSSBORDER,
    VehicleExtraType.DEDUCTIBLE,
    VehicleExtraType.WINDOW_AND_MOBILITY_INSURANCE,
    VehicleExtraType.YOUNG_DRIVER,
    VehicleExtraType.EXTRA_KM,
  ],
  autoAssign: true,
});

const getExtraDto = <T extends EditAutoAssignVehicleExtraDto['type']>(type: T): EditExtraDto => {
  const priceRanges = vehicleExtras.value
    ?.filter((item) => item.type === type && item.key === null)
    .sort((a, b) => (a.autoAssignValueGte ?? 0) - (b.autoAssignValueGte ?? 0))
    .map((item, i, extras) => {
      const diff = item.type === VehicleExtraType.EXTRA_KM ? 0.01 : 1;
      const nextExtra = extras[i + 1];
      const toPrice = nextExtra?.autoAssignValueGte ? nextExtra.autoAssignValueGte - diff : null;
      return {
        id: item.id,
        fromPrice: item.autoAssignValueGte ?? 0,
        toPrice,
        options: item.options.map((option) => ({
          pricingType: option.pricingType,
          id: option.id,
          price: option.price,
          pricePerDay: option.pricePerDay,
          value: option.value,
        })),
      };
    });
  return {
    type,
    priceRanges: priceRanges ?? [],
  };
};

watchEffect(() => {
  uiStore.setHeaderTitle(t('editExtras'));
});

const hasChanged: boolean[] = [];
usePreventLeavingUnsavedForm(() => hasChanged.some((changed) => changed));
</script>

<i18n lang="json">
{
  "en": {
    "editExtras": "Edit extras",
    "additionalDrivers": "Additional drivers",
    "crossBorder": "Cross border",
    "deductible": "Deductible",
    "windowAndMobilityInsurance": "Window and mobility insurance",
    "youngDriver": "Young Driver",
    "extraKm": "Extra KM"
  },
  "de": {
    "editExtras": "Extras bearbeiten",
    "additionalDrivers": "Zusatzfahrer",
    "crossBorder": "Auslandsfahrt",
    "deductible": "Selbstbeteiligung",
    "windowAndMobilityInsurance": "Fenster- und Reifenschutz & Mobilitätsgarantie",
    "youngDriver": "Jungfahrer",
    "extraKm": "Extra KM"
  },
  "es": {
    "editExtras": "Editar extras",
    "additionalDrivers": "Conductores adicionales",
    "crossBorder": "Salida al extranjero",
    "deductible": "Franquicia",
    "windowAndMobilityInsurance": "Cristales y seguro de movilidad",
    "youngDriver": "Conductor joven",
    "extraKm": "Km extra"
  }
}
</i18n>
